/**
 * Order document (zamówienie) payload schema — offer-order-document-export, REQ-9.6.
 *
 * `OrderDocumentPayloadSchema` is the typed input consumed by
 * `renderOrderDocument` (packages/documents). It is assembled server-side by
 * `OrderDocumentService` (apps/api) from the offer, its client, its
 * salesperson, and the accepted-units read query (packages/db).
 *
 * Money representation: `clientPriceNet`/`discountPercent` are plain JS
 * `number`, matching `OfferSnapshotUnitPriceSchema` (offer-version.ts) and
 * the `offer_versions.snapshotData` JSONB storage shape those values are
 * sourced from — the raw Postgres `numeric` columns are already converted to
 * `number` before they ever reach the snapshot (see
 * `OfferVersionsService`'s `parseNumericOrNull`, and the `::float8` cast
 * precedent in `packages/db/src/queries/offer-versions/list-versions-by-offer.ts`).
 * No further string/number coercion happens at this boundary.
 *
 * `clientPriceNet` is nullable: an accepted unit awaiting a supplier price
 * still generates a document (bug fix, offer-order-document-export) — the
 * renderer prints an em dash for that line's price/value cells and excludes
 * it from the footer total rather than refusing to generate.
 */
import { z } from 'zod';

// ─── Period ──────────────────────────────────────────────────────────────

const OrderDocumentPeriodSchema = z.object({
  from: z.string(),
  to: z.string(),
});

// ─── Line item ───────────────────────────────────────────────────────────

export const OrderDocumentLineSchema = z.object({
  grwId: z.string(),
  city: z.string(),
  address: z.string(),
  format: z.string(),
  dimensions: z.string(),
  lighting: z.string(),
  // Omitted entirely (not just null) when the caller lacks supplier.view —
  // the renderer drops the Dostawca/Nr dostawcy columns, it does not blank them.
  supplierName: z.string().nullable().optional(),
  supplierNumber: z.string().nullable().optional(),
  periods: z.array(OrderDocumentPeriodSchema),
  // Null when the accepted unit has no supplier price yet — the document is
  // still generated (bug fix, offer-order-document-export); it no longer
  // refuses generation the way the initial 409 UNPRICED_ACCEPTED_UNITS did.
  clientPriceNet: z.number().nullable(),
  discountPercent: z.number().nullable(),
});
export type OrderDocumentLine = z.infer<typeof OrderDocumentLineSchema>;

// ─── Header ──────────────────────────────────────────────────────────────

export const OrderDocumentHeaderSchema = z.object({
  // Null until ACCEPTED -> CONFIRMED; renderer prints PROJEKT in its place.
  campaignNumber: z.string().nullable(),
  clientName: z.string(),
  // offers.brand — the offer's own nullable brand, never clients.brands.
  brand: z.string().nullable(),
  offerTitle: z.string(),
  salespersonName: z.string(),
  salespersonEmail: z.string(),
  generatedAt: z.coerce.date(),
  validUntil: z.coerce.date(),
  currency: z.string(),
  // Resolved once per request via the existing supplier.view redaction
  // primitive; gates whether the renderer includes the supplier columns.
  canViewSupplier: z.boolean(),
});
export type OrderDocumentHeader = z.infer<typeof OrderDocumentHeaderSchema>;

// ─── Payload ─────────────────────────────────────────────────────────────

export const OrderDocumentPayloadSchema = z.object({
  header: OrderDocumentHeaderSchema,
  // A zamówienie with zero lines is never valid — defense-in-depth mirroring
  // the service's 409 NO_ACCEPTED_UNITS rule, so any future direct caller
  // (e.g. a worker job) cannot bypass it by skipping the API service.
  lines: z.array(OrderDocumentLineSchema).min(1),
});
export type OrderDocumentPayload = z.infer<typeof OrderDocumentPayloadSchema>;
